import React from "react";
import { Routes, Route } from "react-router-dom";

import AuthPage from "./AuthPage";
import LoginPage from "./LoginPage";
import InvalidProgramPage from "./InvalidProgramPage";
import SomethingWentWrongPage from "./SomethingWentWrongPage";
import DynamicScreen from "./DynamicScreen/DynamicScreen";
import ProgramDocumentsScreen from "./ProgramDocumentsScreen";
import ProtectedRoute from "src/components/app/ProtectedRoute";
import ViewRecordScreen from "./RecordsScreen/ViewRecordScreen";
import LearnContentListScreen from "./LearnContent/LearnContentListScreen";
import LearnContentDetailsScreen from "./LearnContent/LearnContentDetailsScreen";
import { BookConsultationScreen } from "./Appointments/BookConsultationScreen";
import ScheduleAppointmentScreen from "./Appointments/CoachBookingScreen";
import BookLabTestScreen from "./BookLabTest/BookLabTestScreen";
import CategoryTestListView from "src/components/app/lab-tests/CategoryTestListView";
import LabTestCheckoutScreen from "./BookLabTest/LabTestCheckoutScreen";
import ScheduleLabTest from "./BookLabTest/ScheduleLabTest";
import LabTestSearchScreen from "./BookLabTest/LabTestSearchScreen";
import AddCompleteAddress from "./BookLabTest/AddCompleteAddress";
import SearchLocation from "./BookLabTest/SearchLocation";
import ApplyCouponScreen from "./BookLabTest/ApplyCouponScreen";
import LabTestSummaryScreen from "./BookLabTest/LabTestSummaryScreen";
import CongratulationScreen from "./BookLabTest/CongratulationScreen";
import OrderHistoryScreen from "./BookLabTest/Orders/OrderHistoryScreen";
import OrderDetailsScreen from "./BookLabTest/Orders/OrderDetailsScreen";
import ReasonListScreen from "./BookLabTest/Orders/ReasonListScreen";
import RescheduleSummaryScreen from "./BookLabTest/Orders/RescheduleSummaryScreen";
import CycleDetailsScreen from "./Journey/CycleDetailsScreen";
import JourneyDetailsScreen from "./Journey/JourneyDetailsScreen";
import ProgramDetailsScreen from "./Program/ProgramDetailsScreen";
import ClaimMilestoneScreen from "./Program/ClaimMilestoneScreen";
import MilestoneDocumentsScreen from "./Program/MilestoneDocumentsScreen";
import RescheduleCycleScreen from "./Journey/RescheduleCycleScreen";
import SymptomLogsScreen from "src/components/app/insights/symptoms/SymptomLogsScreen";
import ClaimDetailsScreen from "./Journey/ClaimDetailsScreen";
import SearchUserLocation from "./Journey/ClaimVivitra/SearchUserLocation";
import ChooseCompleteAddress from "./Journey/ClaimVivitra/ChooseCompleteAddress";
import SuccessScreen from "./SuccessScreen/SuccessScreen";
import WalletScreen from "./WalletScreen";
import ChatScreen from "./ChatScreen";
import ProfileScreen from "./Profile/ProfileScreen";
import EditProfileScreen from "./Profile/EditProfileScreen";
import AddCareGiver from "./Profile/AddCareGiver";
import ConsultationPaymentScreen from "./Appointments/ConsultationPaymentScreen";
import BCAShowAllDataScreen from "./BCAShowAllDataScreen";
import SmartScale from "./Devices/SmartScale";
import ContinuousGlucoseMonitor from "./Devices/ContinuousGlucoseMonitor";
import CGMStatusScreen from "./Devices/cgm/CGMStatusScreen";
import InsightsScreen from "./InsightsScreen/InsightsScreen";
import CGMNeedHelpScreen from "./Devices/cgm/CGMNeedHelpScreen";
import CGMChatWithExperts from "./Devices/cgm/CGMChatWithExperts";
import CGMConnectGuideScreen from "./Devices/cgm/CGMConnectGuideScreen";

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invalid-program" element={<InvalidProgramPage />} />
      <Route
        path="/something-went-wrong"
        element={<SomethingWentWrongPage />}
      />
      <Route
        path="/:support-program-key/smart-scale-connection"
        element={
          <ProtectedRoute>
            <SmartScale />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/bca-show-all-data"
        element={
          <ProtectedRoute>
            <BCAShowAllDataScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/insights-screen"
        element={
          <ProtectedRoute>
            <InsightsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/:screen-name"
        element={
          <ProtectedRoute>
            <DynamicScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/program-documents"
        element={
          <ProtectedRoute>
            <ProgramDocumentsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/records/preview"
        element={
          <ProtectedRoute>
            <ViewRecordScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/learn-content"
        element={
          <ProtectedRoute>
            <LearnContentListScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/learn-content/:content-id"
        element={
          <ProtectedRoute>
            <LearnContentDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-consultation"
        element={
          <ProtectedRoute>
            <BookConsultationScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-consultation/select-slot/:coach-key"
        element={
          <ProtectedRoute>
            <ScheduleAppointmentScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-consultation/payment/:coach-key"
        element={
          <ProtectedRoute>
            <ConsultationPaymentScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test"
        element={
          <ProtectedRoute>
            <BookLabTestScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/:catalog-name"
        element={
          <ProtectedRoute>
            <CategoryTestListView viewAll={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/checkout"
        element={
          <ProtectedRoute>
            <LabTestCheckoutScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/select-time-slot"
        element={
          <ProtectedRoute>
            <ScheduleLabTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/search-lab-tests"
        element={
          <ProtectedRoute>
            <LabTestSearchScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/search-address"
        element={
          <ProtectedRoute>
            <SearchLocation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/add-complete-address"
        element={
          <ProtectedRoute>
            <AddCompleteAddress />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/apply-coupon"
        element={
          <ProtectedRoute>
            <ApplyCouponScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/summary"
        element={
          <ProtectedRoute>
            <LabTestSummaryScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/reschedule-summary"
        element={
          <ProtectedRoute>
            <RescheduleSummaryScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/congratulation"
        element={
          <ProtectedRoute>
            <CongratulationScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/order-details"
        element={
          <ProtectedRoute>
            <OrderDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/order-history"
        element={
          <ProtectedRoute>
            <OrderHistoryScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/book-lab-test/reason-list/:action-type"
        element={
          <ProtectedRoute>
            <ReasonListScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/program-details"
        element={
          <ProtectedRoute>
            <ProgramDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/program-details/milestone-documents/:milestone-key"
        element={
          <ProtectedRoute>
            <MilestoneDocumentsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/program-details/claim-details"
        element={
          <ProtectedRoute>
            <ClaimMilestoneScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/journey-details"
        element={
          <ProtectedRoute>
            <JourneyDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/journey-details/cycle-details"
        element={
          <ProtectedRoute>
            <CycleDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/journey-details/cycle-details/reschedule-cycle"
        element={
          <ProtectedRoute>
            <RescheduleCycleScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/journey-details/claim-details"
        element={
          <ProtectedRoute>
            <ClaimDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/claim-vivitra/search-address"
        element={
          <ProtectedRoute>
            <SearchUserLocation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/claim-vivitra/add-complete-address"
        element={
          <ProtectedRoute>
            <ChooseCompleteAddress />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/symptom-logs"
        element={
          <ProtectedRoute>
            <SymptomLogsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/wallet"
        element={
          <ProtectedRoute>
            <WalletScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/success"
        element={
          <ProtectedRoute>
            <SuccessScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/profile-screen"
        element={
          <ProtectedRoute>
            <ProfileScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/profile-screen/edit"
        element={
          <ProtectedRoute>
            <EditProfileScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:support-program-key/profile-screen/add-care-giver"
        element={
          <ProtectedRoute>
            <AddCareGiver />
          </ProtectedRoute>
        }
      />

      {/* CGM Routes */}

      <Route
        path="/:support-program-key/cgm-connection"
        element={
          <ProtectedRoute>
            <ContinuousGlucoseMonitor />
          </ProtectedRoute>
        }
      />

      <Route
        path="/:support-program-key/cgm-status"
        element={
          <ProtectedRoute>
            <CGMStatusScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/:support-program-key/cgm-need-help"
        element={
          <ProtectedRoute>
            <CGMNeedHelpScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/:support-program-key/cgm-chat-with-experts"
        element={
          <ProtectedRoute>
            <CGMChatWithExperts />
          </ProtectedRoute>
        }
      />

      <Route
        path="/:support-program-key/cgm-connect-guide"
        element={
          <ProtectedRoute>
            <CGMConnectGuideScreen />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
